import React, { createContext, useState, useEffect, useCallback } from 'react';
import { gql } from '@apollo/client';
import client from '../apollo';
import { isTokenNearExpiry } from '../utils/jwt';

export const AuthContext = createContext();

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($token: String) {
    refreshToken(token: $token) {
      token
      refreshToken
    }
  }
`;

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (storedToken) {
      setToken(storedToken);
      setRefreshToken(storedRefreshToken);
      setUser(JSON.parse(localStorage.getItem('user') || '{}'));

      // Proactively refresh on cold start if the access token is near
      // expiry and we still hold a refresh token. Eliminates the first
      // GraphQL request failing after long idle (e.g. >7d).
      if (storedRefreshToken && isTokenNearExpiry(storedToken, 60)) {
        // Defer so we don't block render; runs in the background.
        // refreshAccessToken uses the stored refresh token from localStorage.
        // Use a shallow call rather than the React-state hook reference
        // to avoid stale-closure issues during initial mount.
        (async () => {
          try {
            const { data } = await client.mutate({
              mutation: REFRESH_TOKEN_MUTATION,
              variables: { token: storedRefreshToken },
            });
            if (data?.refreshToken?.token) {
              const newToken = data.refreshToken.token;
              const newRefresh = data.refreshToken.refreshToken || storedRefreshToken;
              setToken(newToken);
              setRefreshToken(newRefresh);
              localStorage.setItem('token', newToken);
              localStorage.setItem('refreshToken', newRefresh);
            }
          } catch {
            // swallow on cold start — apollo.js will handle mid-session failures
          }
        })();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((newToken, newRefreshToken, userData) => {
    setToken(newToken);
    setRefreshToken(newRefreshToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const storedRefresh = localStorage.getItem('refreshToken');
    if (!storedRefresh) {
      logout();
      return null;
    }

    try {
      const { data } = await client.mutate({
        mutation: REFRESH_TOKEN_MUTATION,
        variables: { token: storedRefresh },
      });

      if (data?.refreshToken?.token) {
        const newToken = data.refreshToken.token;
        const newRefresh = data.refreshToken.refreshToken || storedRefresh;
        setToken(newToken);
        setRefreshToken(newRefresh);
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefresh);
        return newToken;
      }
      logout();
      return null;
    } catch (err) {
      // Network/transport error → rethrow so callers can retry with
      // backoff without losing the session. Hard logout is handled by
      // apollo.js when the refresh token is explicitly rejected by the
      // backend (GraphQL auth error, not network).
      throw err;
    }
  }, [logout]);

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider
      value={{ token, refreshToken, user, login, logout, refreshAccessToken, isAuthenticated, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};
